import styled from "styled-components";
import { motion } from "framer-motion";
import { useMemo, useState } from "react";
import LoadingScreen from "../components/common/LoadingScreen";
import ErrorScreen from "../components/common/ErrorScreen";
import { useDelayedLoading } from "../hooks/useDelayedLoading";
import emailjs from "@emailjs/browser";
import { EMAIL_CONFIG, isEmailConfigured } from "../config/emailConfig";
import SEO from "../components/SEO";
import StructuredData, {
  LocalBusinessSchema,
  ServiceSchema,
} from "../components/StructuredData";

import image1 from "../assets/images/imgPool/c1.jpeg";
import image2 from "../assets/images/imgPool/c2.jpeg";
import image3 from "../assets/images/imgPool/c3.jpeg";

const About = () => {
  // Add state for contact form
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    inquiryType: "",
    message: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Preload images for About page
  const loadingPromises = useMemo(() => {
    const imagesToLoad = [image1, image2, image3];

    return imagesToLoad.map((imageUrl) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.src = imageUrl;
        img.onload = resolve;
        img.onerror = resolve;
      });
    });
  }, []);

  // Use the custom hook
  const { isLoading: pageLoading, error } = useDelayedLoading(loadingPromises);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEmailConfigured()) {
      setSubmitStatus("error");
      return;
    }
    setIsLoading(true);
    setSubmitStatus(null);

    try {
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        inquiry_type: formData.inquiryType,
        message: formData.message,
        to_email: EMAIL_CONFIG.recipientEmail,
      };

      await emailjs.send(
        EMAIL_CONFIG.serviceId,
        EMAIL_CONFIG.templateId,
        templateParams,
        EMAIL_CONFIG.publicKey
      );

      setSubmitStatus("success");
      setFormData({
        name: "",
        email: "",
        phone: "",
        inquiryType: "",
        message: "",
      });
    } catch (error) {
      console.error("Error sending contact form:", error);
      setSubmitStatus("error");
    } finally {
      setIsLoading(false);
    }
  };

  if (error) {
    return <ErrorScreen error={error} />;
  }

  if (pageLoading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <SEO
        title="About Us - 25+ Years of Excellence in Crystal Chandeliers"
        description="Learn about Chandelier Luxe's 25+ years of expertise in luxury crystal chandeliers. Professional installation, restoration, and maintenance services. Over 1000 successful installations in Johannesburg and South Africa."
        keywords="chandelier company Johannesburg, crystal chandelier experts, chandelier installation professionals, chandelier restoration services, luxury lighting specialists South Africa, chandelier history, premium crystal suppliers"
        url="/about"
        image="/assets/images/imgPool/c1.jpeg"
      />
      <StructuredData data={LocalBusinessSchema} />
      <StructuredData data={ServiceSchema} />
      <AboutContainer>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <HeroSection>
            <HeroImage src={image1} alt="Chandelier Luxe Showroom" />
            <h1>Welcome to Chandelier Luxe</h1>
          </HeroSection>

          <Section>
            <h2>Our Story</h2>
            <FlexContainer reverse>
              <div>
                <p>
                  With over 25 years of experience, Chandelier Luxe has become a
                  trusted leader in the crystal chandelier industry. From
                  delivering and installing a wide range of exquisite
                  chandeliers to participating in all major exhibitions, we have
                  consistently showcased our commitment to quality and
                  innovation. Our active presence in these events keeps us at
                  the forefront of industry trends, allowing us to continually
                  refine our craft and offer the latest designs that blend
                  timeless elegance with modern sophistication.
                </p>
                <br></br>
                <p>
                  At the core of our business is an unwavering dedication to
                  customer satisfaction. We believe in providing a personalized
                  experience, ensuring every chandelier perfectly complements
                  the unique style and space of our clients. From the meticulous
                  selection of the finest materials to the expert craftsmanship
                  that goes into each piece, our commitment to excellence shines
                  through. Our goal is to illuminate your spaces with
                  chandeliers that not only captivate but also stand as lasting
                  symbols of luxury and grace.
                </p>
              </div>
              <ImageContainer>
                <img
                  src={image2}
                  alt="Chandelier Luxe Showroom"
                  onError={(e) => {
                    e.target.src = image3;
                    console.error("Image failed to load:", image2);
                  }}
                />
              </ImageContainer>
            </FlexContainer>
          </Section>

          <Section alternate>
            <div id="expertise">
              <h2>Our Expertise</h2>
              <ServicesGrid>
                <ServiceCard>
                  <h3>Installation</h3>
                  <p>
                    Professional mounting and setup with attention to detail
                  </p>
                </ServiceCard>
                <ServiceCard>
                  <h3>Restoration</h3>
                  <p>Bringing vintage pieces back to their original glory</p>
                </ServiceCard>
                <ServiceCard>
                  <h3>Maintenance</h3>
                  <p>Regular cleaning and upkeep services</p>
                </ServiceCard>
                <ServiceCard>
                  <h3>Custom Design</h3>
                  <p>Bespoke solutions for unique spaces</p>
                </ServiceCard>
              </ServicesGrid>
            </div>
          </Section>

          <Section>
            <h2>Our Crystals</h2>
            <FlexContainer>
              <div>
                <p>
                  At Chandelier Luxe, we pride ourselves on offering standard
                  crystals that are of the highest quality. Each crystal is
                  carefully selected to ensure brilliance, clarity, and
                  durability, making our chandeliers stunning focal points in
                  any space. Our standard collection reflects our commitment to
                  excellence, providing customers with luxurious options that
                  exude elegance and sophistication.
                </p>
                <br></br>
                <p>
                  For those seeking something truly extraordinary, we offer the
                  option to source premium crystals from renowned brands such as
                  Asfour and Swarovski upon request. These world-class crystals
                  are known for their exceptional craftsmanship and dazzling
                  beauty, adding an extra layer of refinement to our
                  chandeliers. Whether you choose from our standard collection
                  or opt for branded crystals, we ensure that every piece is
                  crafted to perfection, creating a timeless addition to your
                  décor.
                </p>
              </div>
              <ImageContainer>
                <img
                  src={image3}
                  alt="Crystal Collection"
                  onError={(e) => {
                    e.target.src = "fallback-image-path.jpg";
                    console.error("Image failed to load:", image3);
                  }}
                />
              </ImageContainer>
            </FlexContainer>
          </Section>

          <Section alternate>
            <h2>Why Choose Us</h2>
            <FeaturesList>
              <Feature>
                <h3>25+ Years Experience</h3>
                <p>Decades of expertise in luxury lighting</p>
              </Feature>
              <Feature>
                <h3>1000+ Installations</h3>
                <p>Proven track record of successful projects</p>
              </Feature>
              <Feature>
                <h3>Premium Materials</h3>
                <p>Only the finest crystals and components</p>
              </Feature>
            </FeaturesList>
          </Section>

          <Section>
            <h2>Contact Us</h2>
            <ContactContainer>
              <ContactInfo>
                <h3>Get in Touch</h3>
                <p>Phone: (+27) 84 422 9825</p>
                <p>Email: info@chandelierluxe.co.za</p>
                <p>
                  Address: 34 Dion Close, Strathavon, Sandton, Johannesburg,
                  2031
                </p>
              </ContactInfo>
              <ContactForm>
                <form onSubmit={handleSubmit}>
                  {submitStatus === "success" && (
                    <SuccessMessage>
                      Thank you for your message! We'll get back to you soon.
                    </SuccessMessage>
                  )}

                  {submitStatus === "error" && (
                    <ErrorMessage>
                      {!isEmailConfigured()
                        ? "Email is not configured. Please add REACT_APP_EMAILJS_PUBLIC_KEY to your .env file (see .env.example)."
                        : "Sorry, there was an error sending your message. Please try again or contact us directly."}
                    </ErrorMessage>
                  )}

                  <FormGroup>
                    <input
                      type="text"
                      name="name"
                      placeholder="Full Name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <input
                      type="email"
                      name="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </FormGroup>
                  <FormGroup>
                    <select
                      id="inquiryType"
                      name="inquiryType"
                      value={formData.inquiryType}
                      onChange={handleChange}
                    >
                      <option value="" disabled>
                        Select Inquiry
                      </option>
                      <option value="product">Product Interest</option>
                      <option value="installation">Installation Service</option>
                      <option value="restroration">Restoration Service</option>
                      <option value="accessories">Accessories Service</option>
                      <option value="other">Other</option>
                    </select>
                  </FormGroup>
                  <FormGroup>
                    <textarea
                      name="message"
                      placeholder="Your Message"
                      rows="5"
                      value={formData.message}
                      onChange={handleChange}
                      required
                    ></textarea>
                  </FormGroup>
                  <SubmitButton type="submit" disabled={isLoading}>
                    {isLoading ? "Sending..." : "Send Message"}
                  </SubmitButton>
                </form>
              </ContactForm>
            </ContactContainer>
          </Section>
        </motion.div>
      </AboutContainer>
    </>
  );
};

const AboutContainer = styled.div`
  width: 100%;
  margin: 0 auto;
`;

const HeroSection = styled.div`
  position: relative;
  height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;

  h1 {
    font-size: 4.5rem;
    font-family: "Cormorant Garamond", serif;
    font-weight: 300;
    color: #ffffff;
    margin-bottom: 0.5rem;
    line-height: 1.2;
    letter-spacing: 2px;
    text-transform: uppercase;
    position: absolute;
    bottom: 200px;
    width: 100%;
    text-align: center;
    background: linear-gradient(45deg, #ffffff 0%, #e2c6a1 60%, #c0a080 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-shadow: 0 0 40px rgba(192, 160, 128, 0.4),
      0 0 20px rgba(255, 255, 255, 0.2);

    @media (max-width: 768px) {
      font-size: 2.8rem;
    }
  }
`;

const HeroImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: -1;
  filter: brightness(0.7);
`;

const Section = styled.section`
  padding: 5rem 2rem;
  max-width: ${(props) => props.theme.maxWidth || "1200px"};
  margin: 0 auto;
  background-color: ${(props) =>
    props.alternate
      ? "var(--color-background-alt)"
      : "var(--color-background)"};

  h2 {
    text-align: center;
    margin-bottom: 3rem;
    color: var(--color-primary);
    font-size: 2.5rem;
    position: relative;

    &:after {
      content: "";
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 3px;
      background: var(--color-accent);
    }
  }
`;

const FlexContainer = styled.div`
  display: flex;
  gap: 4rem;
  align-items: stretch;
  min-height: 350px;
  flex-direction: ${(props) => (props.reverse ? "row-reverse" : "row")};

  > div {
    flex: 1;
  }

  > div:first-child {
    flex: 0.8;
    padding-right: 2rem;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    min-height: auto;

    > div:first-child {
      flex: 1;
      padding-right: 0;
    }
  }
`;

const ImageContainer = styled.div`
  flex: 1;
  min-height: 250px;
  max-height: 500px;

  img {
    width: 100%;
    height: 100%;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    transition: transform 0.3s ease;
    object-fit: cover;
    display: block;
    min-height: inherit;

    &:hover {
      transform: scale(1.02);
    }
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 1rem;
`;

const ServiceCard = styled.div`
  padding: 2.5rem;
  border-width: 2px;
  border-style: solid;
  border-color: ${(props) => props.theme.colors.accent};
  background: ${(props) => props.theme.colors.primary};
  border-radius: 12px;

  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  h3 {
    color: ${(props) => props.theme.colors.accent};
    margin-bottom: 1rem;
    font-size: 1.5rem;
  }

  p {
    color: ${(props) => props.theme.colors.text};
    line-height: 1.6;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.8);
  }
`;

const FeaturesList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContactContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Feature = styled.div`
  padding: 2.5rem;
  background: ${(props) => props.theme.colors.primary};
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  text-align: center;
  transition: transform 0.3s ease;

  h3 {
    margin-bottom: 1rem;
    color: ${(props) => props.theme.colors.accent};
    font-size: 1.8rem;
  }

  p {
    color: ${(props) => props.theme.colors.text};
    line-height: 1.6;
  }

  &:hover {
    transform: translateY(-5px);
  }
`;

const ContactInfo = styled.div`
  padding: 2.5rem;
  background: ${(props) => props.theme.colors.primary};
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);

  h3 {
    color: ${(props) => props.theme.colors.accent};
    margin-bottom: 1.5rem;
    font-size: 1.8rem;
  }

  p {
    color: ${(props) => props.theme.colors.text};
    margin-bottom: 1rem;
    line-height: 1.6;

    &:hover {
      color: ${(props) => props.theme.colors.accent};
    }
  }
`;

const ContactForm = styled.div`
  padding: 2.5rem;
  background: var(--color-background);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  input,
  select,
  textarea {
    width: 100%;
    padding: 0.8rem;
    border: 1px solid var(--color-border, #ddd);
    border-radius: 6px;
    font-size: 1rem;
    background: var(--color-background);
    color: var(--color-text);
    transition: border-color 0.3s ease;

    &:focus {
      outline: none;
      border-color: var(--color-primary);
    }
  }

  select {
    option {
      background: #2c3e50; // Dark blue-grey background
      color: #ffffff; // White text
      padding: 12px; // Add more padding for better clickability

      &:hover {
        background: #34495e; // Slightly lighter blue-grey for hover
      }
    }
  }

  textarea {
    resize: vertical;
  }
`;

const SubmitButton = styled.button`
  background: var(--color-primary);
  color: white;
  padding: 1rem 2rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover:not(:disabled) {
    background: var(--color-primary-dark, #0056b3);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: #22c55e;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  text-align: center;
  font-size: 0.9rem;
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #ef4444;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  text-align: center;
  font-size: 0.9rem;
`;

export default About;
